<template>
  <div class="text-left justify-start align-start">
    <div class="d-flex justify-space-between align-center">
      <span class="donation-flow__step-title">
        {{ t('donationGuide.paymentDeliveryDonationDetails.title') }}
      </span>
      <span class="step-indicator">{{ t('donationGuide.progress.mobileText', { current: 2, total: 3 }) }}</span>
    </div>
    <div class="mt-8">
      <donation-amount-selection-component
        v-model="donationDetails.paymentSpecification.causeAmount"
        :admin-fee="donationDetails.paymentSpecification.adminAmount"
        :display-fee="!adminPercentage"
        :selectable-amounts="selectableAmounts"
        :min="donationSettings.min"
        :max="donationSettings.max"
        :fee-regulation-url="feeRegulationUrl"
        data-cy="donation-amount-selection-component"
        @has-error="hasAmountError = $event"
      />
    </div>

    <!-- Donation Summary Section -->
    <div class="donation-summary mt-4 pb-2">
      <div class="donation-summary__row">
        <span class="donation-summary__label">{{
          t('donationGuide.payment.summary.yourDonation')
        }}</span>
        <span class="donation-summary__value">{{
          formatCurrency(donationDetails.paymentSpecification.causeAmount)
        }}</span>
      </div>
      <div class="donation-summary__row">
        <span class="donation-summary__label">
          {{ t('donationGuide.payment.summary.fee') }}
          <v-tooltip location="top" :open-delay="0" :close-delay="1000" :max-width="300" color="white">
            <template #activator="{ props }">
              <v-icon v-bind="props" size="small" class="ml-1 fee-info-icon">mdi-information</v-icon>
            </template>
            <span>{{ t('donationDetails.form.adminFee.hint') }}</span>
            <!-- <span v-if="feeRegulationUrl">
              <br />{{ t('donationGuide.payment.donationAmountSelection.fee.info') }}
              <a
                :href="feeRegulationUrl"
                target="_blank"
                rel="noopener"
                style="color: #017481; text-decoration: underline"
              >
                {{ t('donationGuide.payment.donationAmountSelection.fee.hyperlink') }}
              </a>
            </span> -->
          </v-tooltip>
        </span>
        <span class="donation-summary__value"
          >+ {{ formatCurrency(donationDetails.paymentSpecification.adminAmount) }}</span
        >
      </div>
      <v-divider class="my-4"/>
      <div class="donation-summary__row donation-summary__row--total mt-4">
        <span class="donation-summary__label">{{
          t('donationGuide.payment.summary.total')
        }}</span>
        <span class="donation-summary__value">{{ formatCurrency(totalAmountCalculated) }}</span>
      </div>
    </div>

    <div class="mt-6">
      <payment-method-select-component
        v-model="donationDetails.paymentSpecification.paymentMethod"
        :payment-methods="requestSession.paymentMethods"
        class="mt-8"
        hide-divider
        data-cy="paymentdelivery-view-select-component"
        @change="getAdminFee"
      />

      <div v-if="isMemoryGift" class="mt-8">
        <option-label
          :title="t('donationDetails.form.deliveryMethod.label')"
          class="mb-2"
        />
        <delivery-method-select-component
          v-model="donationDetails.deliveryMethod"
          :delivery-methods="deliveryMethodsRef"
          @change="onDeliveryMethodChange"
        />
      </div>
    </div>

    <v-card class="pt-2" flat>
      <v-card-text class="pa-0">
        <v-form
          ref="paymentForm"
          v-model="validPaymentDetails"
          class="mb-3"
          data-cy="paymentdelivery-view-form"
          lazy-validation
        >
          <v-slide-y-transition>
            <div v-if="isEmailInvoice">
              <option-label :title="t('donorDetails.form.invoice.label') + '*'" />
              <v-text-field
                v-model="donor.invoiceEmail"
                :persistent-hint="true"
                :validate-on-blur="true"
                :placeholder="t('donorDetails.form.invoice.placeholder')"
                :hint="t('donationDetails.form.paymentMethod.invoiceEmailHint')"
                :rules="[rules.email, rules.requiredField(t('donorDetails.form.invoice.label'))]"
                data-cy="paymentdelivery-view-invoice-email"
                persistent-placeholder
                clearable
                required
                class="mb-3"
              />
            </div>
          </v-slide-y-transition>
          <v-slide-y-transition>
            <div v-if="isPhoneBased">
              <option-label :title="mobileLabel + '*'" />
              <v-text-field
                v-model="donor.mobileNumber"
                :hint="t('donationDetails.form.mobileNumber.hint')"
                :placeholder="t('donorDetails.form.mobileNumber.label')"
                :persistent-hint="true"
                :rules="[rules.mobilePhoneNumber, rules.requiredField(mobileLabel)]"
                :validate-on-blur="true"
                persistent-placeholder
                clearable
                class="mb-3"
                data-cy="paymentdelivery-view-mobilenumber"
                required
                @keydown.space.prevent
              />
            </div>
          </v-slide-y-transition>

          <div v-if="isMemoryGift">
            <v-slide-y-transition>
              <div v-if="deliveryIsEmail">
                <option-label :title="t('donationDetails.form.recipient.name') + '*'" />
                <v-text-field
                  v-model="donationDetails.recipient.name"
                  :hint="t('donationDetails.form.recipient.hint')"
                  :placeholder="t('donationDetails.form.recipient.name')"
                  persistent-placeholder
                  :rules="[rules.requiredField(t('donationDetails.form.recipient.name'))]"
                  :validate-on-blur="true"
                  clearable
                  class="mb-3"
                  data-cy="paymentdelivery-view-namefield"
                  required
                />
              </div>
            </v-slide-y-transition>
            <v-slide-y-transition>
              <div v-if="deliveryIsEmail">
                <option-label :title="t('donationDetails.form.recipient.email') + '*'" />
                <v-text-field
                  v-model="donationDetails.recipient.email"
                  :hint="t('donationDetails.form.deliveryMethod.emailHint')"
                  persistent-placeholder
                  :placeholder="t('donorDetails.form.email.placeholder')"
                  :rules="[
                    rules.email,
                    rules.requiredField(t('donationDetails.form.recipient.email')),
                  ]"
                  :validate-on-blur="true"
                  clearable
                  class="mb-3"
                  data-cy="paymentdelivery-view-emailfield"
                  required
                  type="email"
                  @keydown.space.prevent
                />
              </div>
            </v-slide-y-transition>
          </div>
        </v-form>
      </v-card-text>

      <v-form
        ref="personalForm"
        v-model="validPersonalDetails"
        class="mb-3"
        data-cy="personaldetails-view-form"
        lazy-validation
      >
        <new-donor-form-component
          v-model:value="donor"
          v-model:error="donorFormError"
          :show-personal-identity-number-field="deductFromMyTaxes"
          :payment-method="donationDetails.paymentSpecification.paymentMethod ?? undefined"
        >
          <template #taxHeader>
            <v-switch
              v-model="deductFromMyTaxes"
              :label="t('donationGuide.paymentDeliveryDonationDetails.deductFromMyTaxes')"
              data-cy="paymentdelivery-view-deduct-from-my-taxes"
              class="deduct-taxes-switch mb-4"
              color="primary"
              @change="trackDeductFromTaxes"
            />
            <p class="tax-deduction-hint">
              {{
                deductFromMyTaxes
                  ? t('donationGuide.paymentDeliveryDonationDetails.deductFromMyTaxesHintOn')
                  : t('donationGuide.paymentDeliveryDonationDetails.deductFromMyTaxesHintOff')
              }}
            </p>
          </template>
        </new-donor-form-component>
      </v-form>
      <donation-flow-buttons
        previous-id="paymentDeliveryViewBackButton"
        next-id="paymentDeliveryViewNextButton"
        previous-data-cy="paymentdelivery-view-previous-btn"
        next-data-cy="paymentdelivery-view-next-btn"
        :next-aria-label="t('aria.label.checkout')"
        :next-label="t('donationGuide.paymentDeliveryDonationDetails.reviewAndConfirm')"
        @previous-step="goToPreviousStep()"
        @next-step="next"
      />
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch } from 'vue';

import { useValidationRules } from '@/composables/useValidationRules';
import { useLabel } from '@/composables/useLabel';
import { useAdminFee } from '@/composables/useAdminFee';
import { RequestSession } from '@/interfaces/requestSession';
import { Donation } from '@/interfaces/donation/donation';
import { Type as PaymentType } from '@/interfaces/economy/paymentMethod';
import { DeliveryMethod, Type } from '@/classes/DeliveryMethod';
import { stepV1, stepV2 } from '@/enums/donationStepOrder';
import { PaymentSpecification } from '@/interfaces/economy/paymentSpecification';
import OptionLabel from '@/components/OptionLabel.vue';
import DonationFlowButtons from '@/components/DonationFlowButtons.vue';
import DeliveryMethodSelectComponent from '@/components/DeliveryMethodSelectComponent.vue';
import PaymentMethodSelectComponent from '@/components/payment/PaymentMethodSelectComponent.vue';
import { Intermediary } from '@/interfaces/intermediary/intermediary';
import { isMemoryGift as isMemoryGiftFunc } from '@/classes/DonationType';
import { DonationGuideVersion } from '@/enums/donationGuideVersion';
import DonationAmountSelectionComponent from '@/components/payment/DonationAmountSelectionComponent.vue';
import { SelectableAmount } from '@/interfaces/payment/selectableAmount';
import NewDonorFormComponent from '@/components/donor/NewDonorFormComponent.vue';
import { Donor } from '@/interfaces/donation/donor';
import TaxReductionInfoDialogComponent from '@/components/taxreduction/TaxReductionInfoDialogComponent.vue';
import matomo from '@/services/matomoService';
import { getDonationSettings } from '@/utils/donationSettings';
import { getCurrencyForLocation } from '@/utils/currency';
import { scrollToFirstInvalid } from '@/utils/formHelpers';
import store from '@/store';

export default defineComponent({
  name: 'DonationDetailsAndPaymentDeliveryView',
  components: {
    TaxReductionInfoDialogComponent,
    NewDonorFormComponent,
    DonationAmountSelectionComponent,
    DeliveryMethodSelectComponent,
    PaymentMethodSelectComponent,
    OptionLabel,
    DonationFlowButtons,
  },
  props: {
    value: {
      type: Object,
      default: null,
    },
    showPrev: {
      type: Boolean,
      default: true,
    },
    compact: Boolean,
  },
  emits: ['nextStep', 'previousStep'],
  setup(props, { emit }) {
    const { t } = useLabel();
    const { calculateAdminFee } = useAdminFee();

    const getDonationStepFlowVersion = computed(
      () => store.state.requestSession?.donationGuideVersion ?? DonationGuideVersion.V1
    );

    const { rules: baseRules } = useValidationRules(t);
    const rules = {
      requiredField: baseRules.value.requiredField,
      email: baseRules.value.email,
      mobilePhoneNumber: baseRules.value.mobilePhoneNumber,
    };

    const valid = ref(true);
    const hasAmountError = ref(false);
    const validPersonalDetails = ref(true);
    const validPaymentDetails = ref(true);
    const dialog = ref(false);
    const paymentForm = ref();
    const personalForm = ref();
    const deliveryMethodsRef = ref<Array<DeliveryMethod>>([]);
    const donationDetails = ref({
      deliveryMethod: null,
      paymentSpecification: {
        paymentMethod: null,
        adminAmount: 0,
        causeAmount: null,
      } as PaymentSpecification,
      recipient: {
        email: '',
        name: '',
        address: {
          address1: '',
          address2: '',
          address3: '',
          city: '',
          country: '',
          postcode: '',
        },
        isFuneralHome: false,
      },
    } as Donation);
    const paymentDeliveryStep = ref<number>(stepV1.PAYMENT_AND_DELIVERY);
    const adminPercentage = ref<number | null>(null);
    const amountValue = ref(0);
    const deductFromMyTaxes = ref(false);
    const donorFormError = ref(false);
    const donor = ref({
      address: {
        address1: '',
        address2: '',
        address3: '',
        city: '',
        country: '',
        postcode: '',
      },
      email: '',
      firstname: '',
      surname: '',
      socialSecurityNumber: '',
      token: '',
      mobileNumber: '',
      invoiceEmail: '',
    } as Donor);

    const donationSettings = computed(() =>
      getDonationSettings(store.state.userLocation ?? 'SWEDEN')
    );

    const selectableAmounts = computed((): SelectableAmount[] => {
      return donationSettings.value.amounts.map((amount, index) => ({
        id: index + 1,
        amount,
      }));
    });

    const steps = computed(() =>
      getDonationStepFlowVersion.value === DonationGuideVersion.V1 ? stepV1 : stepV2
    );

    const isEmailInvoice = computed(() => {
      return (
        donationDetails.value.paymentSpecification.paymentMethod &&
        donationDetails.value.paymentSpecification.paymentMethod.type === PaymentType.INVOICE_EMAIL
      );
    });

    const isPhoneBased = computed(() => {
      return (
        (donationDetails.value.paymentSpecification &&
          donationDetails.value.paymentSpecification.paymentMethod &&
          donationDetails.value.paymentSpecification.paymentMethod.type === PaymentType.SWISH) ||
        (donationDetails.value.paymentSpecification &&
          donationDetails.value.paymentSpecification.paymentMethod &&
          donationDetails.value.paymentSpecification.paymentMethod.type === PaymentType.PAYLINK_SMS)
      );
    });

    const isMailInvoice = computed(() => {
      return (
        donationDetails.value.paymentSpecification.paymentMethod &&
        donationDetails.value.paymentSpecification.paymentMethod.type === PaymentType.INVOICE_POST
      );
    });

    const deliveryIsEmail = computed(() => {
      return (
        donationDetails.value.deliveryMethod &&
        donationDetails.value.deliveryMethod.type === Type.EMAIL
      );
    });

    const isMemoryGift = computed(() => {
      return donation.value?.donationType?.type
        ? isMemoryGiftFunc(donation.value.donationType.type)
        : false;
    });

    const requestSession = computed((): RequestSession => store.state.requestSession!);

    const donation = computed((): Donation => store.state.donation!);

    const currentStep = computed(() => store.state.currentStep);

    // Watcher to populate deliveryMethodsRef from requestSession
    watch(
      () => requestSession.value?.deliveryMethods,
      (newMethods) => {
        if (Array.isArray(newMethods) && newMethods.length > 0) {
          deliveryMethodsRef.value = newMethods;
        }
      },
      { deep: true, immediate: true }
    );

    const mobileLabel = computed((): string => {
      if (
        donationDetails.value.paymentSpecification &&
        donationDetails.value.paymentSpecification.paymentMethod &&
        donationDetails.value.paymentSpecification.paymentMethod.type === PaymentType.PAYLINK_SMS
      ) {
        return t('payment.customerMobileForPayLink') || '';
      } else {
        if (
          requestSession.value &&
          (requestSession.value.incomingChannel === 'Bitnet' ||
            requestSession.value.incomingChannel === 'Bitnet - förmedling')
        ) {
          return t('payment.customerMobileForSwish') || '';
        } else {
          return t('payment.yourMobileForSwish') || '';
        }
      }
    });

    const totalAmountCalculated = computed(() => {
      const raw = donationDetails.value.paymentSpecification.causeAmount;
      const donationAmount = typeof raw === 'number' ? raw : raw ? parseInt(String(raw), 10) : NaN;
      if (Number.isNaN(donationAmount)) return null;
      if (
        donationSettings.value.min <= donationAmount &&
        donationAmount <= donationSettings.value.max
      ) {
        return donationAmount + donationDetails.value.paymentSpecification.adminAmount;
      }
      return null;
    });

    const caseRecipientHasDefaultDelivery = computed(() => {
      return (
        donation.value.donationCase != null &&
        donation.value.donationCase.recipient != null &&
        donation.value.donationCase.recipient.deliveryMethod != null
      );
    });

    const caseRecipientNoCertificateHandling = computed(() => {
      return (
        donation.value.donationCase != null &&
        donation.value.donationCase.recipient != null &&
        donation.value.donationCase.recipient.noCertificateHandling
      );
    });

    const isDirectPaymentAvailable = computed((): boolean => {
      return !!(
        requestSession.value.organizationGroup &&
        requestSession.value.organizationGroup.swedbankPaySettings &&
        requestSession.value.organizationGroup.swedbankPaySettings.id &&
        requestSession.value.organizationGroup.swedbankPaySettings.id > 0
      );
    });

    const feeRegulationUrl = computed((): string | undefined => {
      return !isDirectPaymentAvailable.value
        ? 'https://pennybridge.org/avgift-pennybridge/'
        : undefined;
    });

    const hasSelectedAmount = computed((): boolean => {
      const selectedAmount = donationDetails.value.paymentSpecification.causeAmount || null;
      return !hasAmountError.value && !!selectedAmount && selectedAmount > 0;
    });

    // Populate recipient from the case (or clear to empty) based on the current delivery method.
    const seedRecipientFromCase = () => {
      const email = caseRecipientHasDefaultDelivery.value
        ? donation.value.donationCase.recipient.email
        : '';
      const name = caseRecipientHasDefaultDelivery.value
        ? donation.value.donationCase.recipient.name
        : '';
      if (caseRecipientHasDefaultDelivery.value && !deliveryIsEmail.value) {
        donationDetails.value.recipient = donation.value.donationCase.recipient;
      } else if (deliveryIsEmail.value) {
        donationDetails.value.recipient = {
          email: email,
          name: name,
          address: donationDetails.value.recipient?.address,
          isFuneralHome: false,
        };
      }
    };

    // User changed delivery method: re-seed recipient, then recalc fee.
    const onDeliveryMethodChange = () => {
      seedRecipientFromCase();
      getAdminFee();
    };

    const getAdminFee = async () => {
      if (
        donationDetails.value &&
        donationDetails.value.deliveryMethod &&
        donationDetails.value.paymentSpecification.paymentMethod &&
        donation.value.donationCause &&
        donation.value.donationType
      ) {
        const intermediary: Intermediary | null = requestSession.value.intermediary;
        const currency = getCurrencyForLocation(store.state.userLocation ?? 'SWEDEN');

        const params = {
          deliveryMethodId: donationDetails.value.deliveryMethod.id,
          paymentMethodId: donationDetails.value.paymentSpecification.paymentMethod!.id,
          intermediaryId: intermediary ? intermediary.id : null,
          currency: currency,
        };

        try {
          const data = await calculateAdminFee(
            donation.value.donationCause.id,
            donation.value.donationType.id,
            params
          );
          if (data) {
            if (!!data.adminAmount) {
              donationDetails.value.paymentSpecification.adminAmount = data.adminAmount;
              adminPercentage.value = null;
              donationDetails.value.paymentSpecification.adminPercentage = null;
            } else {
              adminPercentage.value = data.adminPercentage;
              if (adminPercentage.value != null) {
                const calculatedAmount =
                  (Number(donationDetails.value.paymentSpecification.causeAmount) || 0) *
                  adminPercentage.value;
                donationDetails.value.paymentSpecification.adminAmount = calculatedAmount;
                donationDetails.value.paymentSpecification.adminPercentage = adminPercentage.value;
              }
            }
          }
        } catch {
          // If admin fee doesn't exist (common for contributions), set to 0
          donationDetails.value.paymentSpecification.adminAmount = 0;
          donationDetails.value.paymentSpecification.adminPercentage = null;
          adminPercentage.value = null;
        }
      } else {
        // Set admin fee to 0 if conditions aren't met yet (form incomplete)
        if (donationDetails.value?.paymentSpecification) {
          donationDetails.value.paymentSpecification.adminAmount = 0;
        }
      }
    };

    const next = async () => {
      // Manual validation for required fields
      let isValid = true;
      let firstInvalidField: string | null = null;

      // Check if amount is selected
      if (!hasSelectedAmount.value) {
        isValid = false;
        firstInvalidField = 'donation-amount-selection-component';
      }

      // Payment method is required
      if (!donationDetails.value.paymentSpecification?.paymentMethod) {
        isValid = false;
        if (!firstInvalidField) firstInvalidField = 'payment-method-select-component';
      }

      // Delivery method is required
      if (!donationDetails.value.deliveryMethod) {
        isValid = false;
        if (!firstInvalidField) firstInvalidField = 'delivery-method-select';
      }

      // If delivery is email, recipient details are required
      if (donationDetails.value.deliveryMethod?.type === Type.EMAIL) {
        if (
          !donationDetails.value.recipient?.name ||
          !donationDetails.value.recipient.name.trim()
        ) {
          isValid = false;
          if (!firstInvalidField) firstInvalidField = 'namefield';
        }
        if (
          !donationDetails.value.recipient?.email ||
          !donationDetails.value.recipient.email.trim()
        ) {
          isValid = false;
          if (!firstInvalidField) firstInvalidField = 'emailfield';
        }
      }

      if (!isValid) {
        // Trigger validation on both forms to surface all error messages
        try { await paymentForm.value?.validate(); } catch { /* no-op */ }
        try { await personalForm.value?.validate(); } catch { /* no-op */ }
        // Scroll to first invalid field
        if (firstInvalidField) {
          const element = document.querySelector(`[data-cy*="${firstInvalidField}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
        return;
      }

      const { valid: paymentValid } = await paymentForm.value.validate();
      if (!paymentValid) {
        try { scrollToFirstInvalid(paymentForm.value, null); } catch { /* no-op */ }
        return;
      }
      const { valid: personalValid } = await personalForm.value.validate();
      if (!personalValid) {
        try { scrollToFirstInvalid(personalForm.value, null); } catch { /* no-op */ }
        return;
      }

      const donationObj: Donation = donation.value;
      donationObj.deliveryMethod = donationDetails.value.deliveryMethod as DeliveryMethod | null;
      donationObj.paymentSpecification = donationDetails.value
        .paymentSpecification as PaymentSpecification;
      donationObj.donor = donor.value;

      donationObj.paymentSpecification.currencyCode =
        donationObj.paymentSpecification.currencyCode ||
        getCurrencyForLocation(store.state.userLocation ?? 'SWEDEN');

      // Email: use the address entered for certificate delivery. Other methods: link the
      // existing case recipient (funeral home) when present — do not send null, the backend
      // expects donation.recipient when persisting recipient logs during createDonation.
      if (donationObj.deliveryMethod?.type === Type.EMAIL) {
        donationObj.recipient = donationDetails.value.recipient || null;
      } else if (donation.value.donationCase?.recipient) {
        donationObj.recipient = donation.value.donationCase.recipient;
      } else {
        donationObj.recipient = null;
      }
      donationObj.paymentSpecification.paymentMethod =
        donationDetails.value.paymentSpecification.paymentMethod;
      store.commit('updateDonation', donationObj);
      if (donation.value.donor.socialSecurityNumber) {
        matomo.trackEvent('donations_ssn_entered', 'donation_details', 'donations_details_ssn_entered');
      } else {
        matomo.trackEvent('donations_previous_clicked_donation_details', 'donation_details', 'donations_details_payment_checkout_no_ssn');
      }
      matomo.trackEvent(
        'donations_proceeded_to_checkout',
        'donation_details',
        'donations_details_proceeded_to_checkout'
      );
      emit('nextStep');
    };

    const handlePostOrExternalAgentDeliveryMethod = () => {
      const filteredMethods: Array<DeliveryMethod> = requestSession.value.deliveryMethods.slice();
      if (
        isMemoryGift.value &&
        (!requestSession.value.intermediary ||
          (requestSession.value.donationCases && requestSession.value.donationCases.length > 0))
      ) {
        // if there is a recipient set for the case and the recipient has a deliveryMethod with type EXTERNAL_AGENT
        if (
          caseRecipientHasDefaultDelivery.value &&
          (donation.value.donationCase.recipient.deliveryMethod.type === Type.EXTERNAL_AGENT ||
            donation.value.donationCase.recipient.deliveryMethod.type === Type.PRINT)
        ) {
          // First we remove type POST and EMAIL if it exists in the list
          const postIndex = filteredMethods.findIndex((method) => method.type === Type.POST);
          if (postIndex >= 0) {
            filteredMethods.splice(postIndex, 1);
          }
          const emailIndex = filteredMethods.findIndex((method) => method.type === Type.EMAIL);
          if (emailIndex >= 0) {
            filteredMethods.splice(emailIndex, 1);
          }
          // Then we add type EXTERNAL_AGENT if it doesn't exist
          const externalIndex = filteredMethods.findIndex(
            (method) => method.type === Type.EXTERNAL_AGENT
          );
          if (externalIndex < 0) {
            filteredMethods.push({
              id: 4,
              type: Type.EXTERNAL_AGENT,
              name: t('payment.deliveryMethod.externalAgent.name'),
              description: t('payment.deliveryMethod.externalAgent.description'),
              nameTranslationKey: 'deliveryMethod.external.name',
              descriptionTranslationKey: 'deliveryMethod.external.description',
              color: 'secondary',
              icon: 'mdi-hand-heart',
            } as DeliveryMethod);
          }
          // lastly we pre-select first item in the list
          donationDetails.value.deliveryMethod = filteredMethods[0];
        } else if (filteredMethods.length > 1) {
          // otherwise we remove external agent type deliverymethod if it exists and there is another deliverymethod to choose from
          const index = filteredMethods.findIndex((method) => method.type === Type.EXTERNAL_AGENT);
          if (index >= 0) {
            filteredMethods.splice(index, 1);
          }
        }
      }
      deliveryMethodsRef.value = filteredMethods;
    };

    const handleNoCertificateHandling = () => {
      let filteredMethods: Array<DeliveryMethod> = requestSession.value.deliveryMethods.slice();
      if (
        isMemoryGift.value &&
        requestSession.value.donationCases &&
        requestSession.value.donationCases.length > 0
      ) {
        // if there is a recipient set for the case and the recipient has a deliveryMethod with type EXTERNAL_AGENT
        if (caseRecipientNoCertificateHandling.value) {
          // First we remove everything except Email
          filteredMethods = filteredMethods.filter((fm) => fm.type === Type.EMAIL);
          // Then we add type Email if it doesn't exist
          const index = filteredMethods.findIndex((method) => method.type === Type.EMAIL);
          if (index < 0) {
            filteredMethods.push({
              id: 1,
              type: Type.EMAIL,
              name: t('payment.deliveryMethod.email.name'),
              description: t('payment.deliveryMethod.email.description'),
              color: 'primary',
              icon: 'mdi-email-outline',
            } as DeliveryMethod);
          }
        }
      }
    };

    const setDefaultDeliveryMethod = () => {
      // reset deliveryMethod if it is not allowed
      if (donationDetails.value.deliveryMethod) {
        const index = deliveryMethodsRef.value.findIndex(
          (method) => method.type === donationDetails.value.deliveryMethod!.type
        );
        if (index < 0) {
          // set deliveryMethod to null
          donationDetails.value.deliveryMethod = null;
        }
      }

      // Code for pre-select delivery method
      // if (isMemoryGift.value && !donationDetails.value.deliveryMethod) {
      //   // if case recipient has a deliveryMethod that is allowed
      //   if (caseRecipientHasDefaultDelivery.value) {
      //     const index = deliveryMethodsRef.value.findIndex(
      //       (method) => method.type === donation.value.donationCase.recipient.deliveryMethod.type
      //     );
      //     if (index >= 0) {
      //       // set it as default deliveryMethod
      //       donationDetails.value.deliveryMethod = deliveryMethodsRef.value[index];
      //     }
      //   } else {
      //     // otherwise, just set the first deliveryMethod in the list
      //     donationDetails.value.deliveryMethod = deliveryMethodsRef.value[0];
      //   }
      // }

      // // Certificate is not handled, so must be email by default (which should be the first and only element among the delivery methods)
      // if (
      //   isMemoryGift.value &&
      //   requestSession.value.donationCases &&
      //   requestSession.value.donationCases.length > 0 &&
      //   caseRecipientNoCertificateHandling.value
      // ) {
      //   donationDetails.value.deliveryMethod = deliveryMethodsRef.value[0];
      // }

      // Only seed when the form has nothing yet — otherwise this wipes values
      // restored after Back from checkout. Fee recalc is handled by the caller.
      const hasRecipientDetails =
        !!donationDetails.value.recipient?.name?.trim() ||
        !!donationDetails.value.recipient?.email?.trim();
      if (!hasRecipientDetails) {
        seedRecipientFromCase();
      }
    };

    const trackDeductFromTaxes = (value: boolean) => {
      if (value) {
        matomo.trackEvent('donation_details', 'taxes_toggled', 'donations_details_taxes_toggled');
      }
    };

    const goToPreviousStep = () => {
      matomo.trackEvent(
        'donations_previous_clicked_donation_details',
        'donation_details',
        'donations_details_previous_clicked'
      );
      emit('previousStep');
    };

    const formatCurrency = (amount: number | null) => {
      if (amount === null || amount === undefined) return '';
      const currency = getCurrencyForLocation(store.state.userLocation ?? 'SWEDEN');
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'code',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    };

    watch(donation, () => {
      donationDetails.value = {
        deliveryMethod: deliveryMethodsRef.value?.[0] ?? null,
        paymentSpecification: {
          paymentMethod: requestSession.value?.paymentMethods?.[0] ?? null,
          adminAmount: 0,
          causeAmount: donationSettings.value.amounts[1] ?? null,
        } as PaymentSpecification,
        recipient: {
          email: '',
          name: '',
          address: { address1: '', address2: '', address3: '', city: '', country: '', postcode: '' },
          isFuneralHome: false,
        },
      } as Donation;
      donor.value = {
        address: { address1: '', address2: '', address3: '', city: '', country: '', postcode: '' },
        email: '',
        firstname: '',
        surname: '',
        socialSecurityNumber: '',
        token: '',
        mobileNumber: '',
        invoiceEmail: '',
      } as Donor;
      amountValue.value = donationSettings.value.amounts[1] ?? 0;
      deductFromMyTaxes.value = false;
    });

    watch(isEmailInvoice, (val) => {
      if (val && !donor.value.invoiceEmail) {
        donor.value.invoiceEmail = donor.value.email;
      }
    });

    watch(currentStep, (val, oldVal) => {
      if (val == steps.value.PAYMENT_AND_DELIVERY) {
        // Restore recipient name/email committed on Next (Back from checkout).
        // Must run before setDefaultDeliveryMethod so it is not treated as empty.
        const storedRecipient = donation.value?.recipient;
        if (storedRecipient?.name || storedRecipient?.email) {
          donationDetails.value.recipient = {
            ...donationDetails.value.recipient,
            name: storedRecipient.name || '',
            email: storedRecipient.email || '',
          };
        }

        // The order in which these are called is very important! DON'T CHANGE
        handlePostOrExternalAgentDeliveryMethod();
        setDefaultDeliveryMethod();
        handleNoCertificateHandling();

        // Commented out: Don't pre-select payment method
        // if (!donationDetails.value.paymentSpecification.paymentMethod) {
        //   donationDetails.value.paymentSpecification.paymentMethod = requestSession.value.paymentMethods[0];
        // }
        getAdminFee();

        const previousStep = typeof oldVal === 'number' ? oldVal : undefined;
        if (previousStep !== undefined && previousStep < steps.value.PAYMENT_AND_DELIVERY) {
          matomo.trackEvent(
            'donations_donation_details_viewed',
            'donation_details',
            'donations_details_viewed'
          );
        }
      }
    });

    watch(
      () => donationDetails.value.paymentSpecification.causeAmount,
      (after: number, before: number) => {
        if (
          adminPercentage.value != null &&
          after !== before &&
          after >= donationSettings.value.min &&
          after <= donationSettings.value.max
        ) {
          donationDetails.value.paymentSpecification.adminAmount =
            donationDetails.value.paymentSpecification.causeAmount * adminPercentage.value;
        }
      }
    );

    // Watch for payment method changes to recalculate admin fee
    watch(
      () => donationDetails.value.paymentSpecification.paymentMethod,
      (newMethod) => {
        if (newMethod && donationDetails.value.deliveryMethod) {
          getAdminFee();
        }
      }
    );

    // Watch for delivery method changes to recalculate admin fee
    watch(
      () => donationDetails.value.deliveryMethod,
      (newMethod) => {
        if (newMethod && donationDetails.value.paymentSpecification.paymentMethod) {
          getAdminFee();
        }
      }
    );
    const adminFeeInputs = computed(() => ({
      causeId: donation.value?.donationCause?.id || null,
      typeId: donation.value?.donationType?.id || null,
      paymentMethodId:
        donationDetails.value.paymentSpecification.paymentMethod?.id || null,
      deliveryMethodId: donationDetails.value.deliveryMethod?.id || null,
      amount: donationDetails.value.paymentSpecification.causeAmount || null,
    }));

    watch(
      adminFeeInputs,
      (newVals) => {
        if (
          newVals.causeId &&
          newVals.typeId &&
          newVals.paymentMethodId &&
          newVals.deliveryMethodId
        ) {
          getAdminFee();
        }
      },
      { deep: true, immediate: true }
    );

    onMounted(() => {
      // Set default payment method if not already set
      if (
        !donationDetails.value.paymentSpecification.paymentMethod &&
        requestSession.value.paymentMethods?.length > 0
      ) {
        donationDetails.value.paymentSpecification.paymentMethod =
          requestSession.value.paymentMethods[0];
      }

      // Set default delivery method if not already set
      if (!donationDetails.value.deliveryMethod && deliveryMethodsRef.value.length > 0) {
        donationDetails.value.deliveryMethod = deliveryMethodsRef.value[0];
      }
    });

    return {
      t,
      valid,
      hasAmountError,
      validPersonalDetails,
      validPaymentDetails,
      dialog,
      deliveryMethodsRef,
      donationDetails,
      paymentDeliveryStep,
      adminPercentage,
      amountValue,
      deductFromMyTaxes,
      donorFormError,
      donor,
      donationSettings,
      selectableAmounts,
      steps,
      isEmailInvoice,
      isPhoneBased,
      isMailInvoice,
      deliveryIsEmail,
      isMemoryGift,
      requestSession,
      donation,
      paymentForm,
      personalForm,
      currentStep,
      mobileLabel,
      totalAmountCalculated,
      caseRecipientHasDefaultDelivery,
      caseRecipientNoCertificateHandling,
      isDirectPaymentAvailable,
      feeRegulationUrl,
      hasSelectedAmount,
      rules,
      onDeliveryMethodChange,
      getAdminFee,
      next,
      handlePostOrExternalAgentDeliveryMethod,
      handleNoCertificateHandling,
      setDefaultDeliveryMethod,
      trackDeductFromTaxes,
      goToPreviousStep,
      formatCurrency,
    };
  },
});
</script>
<style scoped lang="scss">
.no-sender-name {
  margin: 0;
  padding: 0;
}

.donation-summary {
  padding: 16px 0;

  &__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;

    &:last-child {
      margin-bottom: 0;
    }

    &--total {
      font-size: 16px;
      margin-top: 4px;

      .donation-summary__label,
      .donation-summary__value {
        font-weight: 800;
        color: #141D2C;
      }
    }
  }

  &__label {
    display: flex;
    align-items: center;
    color: #4b5563;
    font-size: 14px;
  }

  &__value {
    color: #111928;
    font-size: 14px;
    font-weight: 400;
    font-family: 'lato', sans-serif;
  }
}

.personal-info-row {
  margin-left: -12px;
  margin-right: -12px;
}

.tax-deduction-hint {
  margin: 0 0 12px;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #4b5563;
}

.donation-summary__value {
  font-weight: 400;
  font-family: "Lato", sans-serif;
  color: #1F2A37;
  line-height: 155%;
}

/* Remove border from the deduct taxes toggle and tint its row to match the dashed container */
.deduct-taxes-switch,
.deduct-taxes-switch :deep(.v-input__control),
.deduct-taxes-switch :deep(.v-selection-control) {
  background-color: #f5f8f7 !important;
}

:deep(.deduct-taxes-switch) .v-input__control {
  border: none !important;
}

/* Make the switch track thicker - gray when not selected */
:deep(.deduct-taxes-switch) .v-switch__track {
  height: 20px !important;
  width: 40px !important;
  opacity: 1 !important;
  background-color: #A8A8A8;
}

:deep(.deduct-taxes-switch) .v-switch__thumb {
  height: 16px !important;
  width: 16px !important;
  background-color: white !important;
}

/* Override bg-primary class to keep thumb white */
:deep(.deduct-taxes-switch) .v-switch__thumb.bg-primary {
  background-color: white !important;
}

.fee-info-icon {
  color: #6b7280; /* default color */
  transition: color 0.2s ease;
  height: 16px;
  padding-left: 5px;
}

.fee-info-icon:hover {
  color: #017481 !important; /* color when hovered */
  cursor: pointer;
  scale: 1.1;
}

</style>
